import React from "react";

import { useState } from "react";

import { useEffect } from "react";

import { useMemo } from "react";

import { useTour } from "@reactour/tour";
import { Typography } from "@mui/material";
import { RectResult } from "@reactour/utils";
import { PositionProps } from "@reactour/popover/dist/index.d";
import TourStepStatus from "./TourStepStatus";
import ReadMore from "../TextDisplayComponents/ReadMore";

type TourStepProps = {
  selector: string;
  startOpenOnce: boolean;
  neverOpenWhen: boolean;
  completedWhenExternalCondition: boolean;
  completedWhenNextButtonClicked: boolean;
  nextButtonText?: string;
  setNextButtonText: (text: string) => void;
  nextButtonAction?: () => void;
  setNextButtonAction: (action: (() => void) | undefined) => void;
  hasForkedTour?: boolean;
  setHasForkedTour?: (hasForkedTour: boolean) => void;
  tourForkAction?: () => void;
  setTourForkAction?: (action: (() => void) | undefined) => void;
  tourForkButtonText?: string;
  setTourForkButtonText?: (text: string) => void;
  necessaryPreviousStepStatus: TourStepStatus;
  restartStepIfCompletionUndone: boolean;
  onFirstStart?: () => void;
  onAnyOpen?: () => void;
  onCompletion?: () => void;
  supraTitle?: string;
  title: string;
  description: string | string[];
  position?: number;
  skipTerms?: string[];
};

//https://docs.react.tours/tour/props
function useTourStep(props: TourStepProps): TourStepStatus {
  const { isOpen, /*currentStep,*/ steps, setIsOpen, /*setCurrentStep,*/ setSteps } = useTour();

  const [everOpened, setEverOpened] = useState(false);
  const [completed, setCompleted] = useState(false);

  const descriptionList = typeof props.description === "string" ? [props.description] : props.description;

  const amCurrentTourStep = useMemo(
    () => steps && steps.length > 0 && steps[0].selector === props.selector,
    [steps, props.selector]
  );

  const completedWhen = props.completedWhenExternalCondition;
  const onCompletion = props.onCompletion;
  const prevIsComplete = props.necessaryPreviousStepStatus.isComplete;
  const restartStepIfCompletionUndone = props.restartStepIfCompletionUndone;

  useEffect(() => {
    if (completedWhen === true && !completed && prevIsComplete) {
      // handles completion from boolean completor
      onCompletion?.();
      props.setHasForkedTour?.(false);
      setCompleted(true);
    } else if (restartStepIfCompletionUndone) {
      setCompleted(false);
    }
  }, [onCompletion, completedWhen, setCompleted, completed, prevIsComplete, restartStepIfCompletionUndone]);

  useEffect(() => {
    if (
      props.neverOpenWhen ||
      props.completedWhenExternalCondition === true ||
      completed ||
      !props.necessaryPreviousStepStatus.isComplete
    ) {
      if (amCurrentTourStep && isOpen) {
        setIsOpen(false);
      }
    } else if (props.startOpenOnce && (!isOpen || !amCurrentTourStep) && setSteps) {
      if (!everOpened) {
        props.onFirstStart?.();
      }
      props.onAnyOpen?.();

      function completeWithNext(): void {
        props.nextButtonAction?.();
        if (props.completedWhenNextButtonClicked === true && !completed && prevIsComplete) {
          // handles completion from next button
          props.onCompletion?.();
          props.setHasForkedTour?.(false);
          setCompleted(true);
        }

        // if (typeof props.completedWhen === "function") {
        //   props.completedWhen(undefined);
        // }
      }
      props.setNextButtonAction(completeWithNext);

      function forkTour(): void {
        props.tourForkAction?.();
        props.setHasForkedTour?.(false);
      }
      props.setTourForkAction?.(forkTour);

      setTimeout(
        () =>
          setSteps([
            {
              selector: props.selector,
              position:
                props.position === undefined
                  ? undefined
                  : (positionProps: PositionProps, prevRect: RectResult) => {
                      const left = positionProps.windowWidth - prevRect.width - (props.position ?? 0);
                      const top = positionProps.windowHeight / 2 - prevRect.height / 2 - (props.position ?? 0);
                      return [left, top];
                    },
              content: (
                <>
                  {props.supraTitle ? (
                    <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: "1.25rem" }}>
                      {props.supraTitle}
                    </Typography>
                  ) : null}
                  <Typography variant="h5" color="info.main">
                    {props.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    color="text.primary"
                    // sx={{ pb: index === descriptionList.length - 1 ? 2.5 : 0.5 }}
                    sx={{ pb: 2.5 }}
                  >
                    <ReadMore
                      embedLearn
                      text={descriptionList}
                      maxCharacters={250}
                      maxSections={1}
                      enterBeforeReadLess
                      skipTerms={props.skipTerms}
                    />
                  </Typography>
                </>
              ),
            },
          ]),
        100
      );
      props.setNextButtonText(props.nextButtonText ?? "");
      props.setHasForkedTour?.(props.hasForkedTour ?? false);
      props.setTourForkButtonText?.(props.tourForkButtonText ?? "");
      setTimeout(() => setIsOpen(true), 300);
      setEverOpened(true);
    }
  }, [
    props.selector,
    props.startOpenOnce,
    props.neverOpenWhen,
    props.completedWhenExternalCondition,
    props.necessaryPreviousStepStatus,
    props.title,
    props.setNextButtonText,
    props.nextButtonText,
    props.skipTerms,
    props.hasForkedTour,
    props.setHasForkedTour,
    props.tourForkAction,
    props.setTourForkAction,
    props.tourForkButtonText,
    props.setTourForkButtonText,
    descriptionList,
    completed,
    steps,
    isOpen,
    setSteps,
    setIsOpen,
    amCurrentTourStep,
    everOpened,
    setEverOpened,
  ]);

  if (completed) {
    if (everOpened) {
      return { status: "completed", isComplete: true };
    } else {
      return { status: "bypassed", isComplete: true };
    }
  } else {
    if (amCurrentTourStep) {
      return { status: "active", isComplete: false };
    } else {
      return { status: "incomplete", isComplete: false };
    }
  }
}

export default useTourStep;
